import { useEffect, useRef } from 'react';
import type { Conversation, BrokerLite } from './types';
import ConversationListItem from './ConversationListItem';

const CHANNELS = [
  { key: '', label: 'All' },
  { key: 'sms', label: 'SMS' },
  { key: 'email', label: 'Email' },
  { key: 'call', label: 'Calls' },
];

interface Props {
  conversations: Conversation[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  total: number;
  channel: string;
  unread: boolean;
  owner: string;
  brokers: Record<string, BrokerLite>;
  query: string;
  onChannel: (c: string) => void;
  onUnread: (b: boolean) => void;
  onOwner: (id: string) => void;
  onQuery: (q: string) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  onRefresh: () => void;
  refreshing: boolean;
}

const chip = (active: boolean) =>
  `text-xs px-2.5 py-1 rounded-full border transition-colors ${
    active ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
  }`;

export default function ConversationList({
  conversations, loading, loadingMore, error, total,
  channel, unread, owner, brokers, query, onChannel, onUnread, onOwner, onQuery,
  selectedId, onSelect, onLoadMore, hasMore, onRefresh, refreshing,
}: Props) {
  const brokerList = Object.entries(brokers || {});
  const sentinel = useRef<HTMLDivElement | null>(null);

  // Infinite scroll: load the next page when the sentinel enters view.
  useEffect(() => {
    if (!hasMore || loading) return;
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) onLoadMore();
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, onLoadMore]);

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="px-3 pt-3 pb-2 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="font-display text-[20px] font-semibold uppercase leading-none tracking-[-.01em] text-gray-900">Conversations</h1>
            <p className="text-[11px] text-gray-400">{total.toLocaleString()} total</p>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="text-xs px-2 py-1 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            title="Pull the latest from GoHighLevel"
          >
            {refreshing ? 'Syncing…' : 'Refresh'}
          </button>
        </div>

        <input
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search name, phone, message…"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
        />

        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {CHANNELS.map((ch) => (
            <button key={ch.key} type="button" onClick={() => onChannel(ch.key)} className={chip(channel === ch.key)}>
              {ch.label}
            </button>
          ))}
          <button type="button" onClick={() => onUnread(!unread)} className={chip(unread)}>
            Unread
          </button>
        </div>

        {brokerList.length > 1 && (
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className="text-[10px] text-gray-400 shrink-0">Owner:</span>
            <button type="button" onClick={() => onOwner('')} className={chip(owner === '')}>All</button>
            {brokerList.map(([id, b]) => (
              <button key={id} type="button" onClick={() => onOwner(owner === id ? '' : id)} className={chip(owner === id)}>
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: b.color }} />
                  {b.name.split(' ')[0]}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {error ? (
          <div className="p-4 text-sm text-red-600">{error}</div>
        ) : loading ? (
          <div className="p-6 text-center text-sm text-gray-400">Loading…</div>
        ) : conversations.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-400">No conversations found</div>
        ) : (
          <>
            {conversations.map((c) => (
              <ConversationListItem key={c.id} c={c} selected={c.id === selectedId} onSelect={onSelect} brokers={brokers} />
            ))}
            <div ref={sentinel} />
            {loadingMore && <div className="p-3 text-center text-xs text-gray-400">Loading more…</div>}
            {!hasMore && conversations.length > 0 && (
              <div className="p-3 text-center text-[11px] text-gray-300">End of conversations</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
